import React, { useCallback, useContext } from 'react';

import Row from 'components/Common/Row';
import Button from 'kingpin/atoms/Button';
import Typography from 'kingpin/atoms/Typography';

import { BonusOverviewContext } from '../../providers/BonusOverviewProvider';
import { AdditionalPaymentsModeContext } from './AdditionalPaymentsModeProvider';
import useFormatPaymentAmount from './useFormatPaymentAmount';

// The driver's bonus pay before the payment, where the record carries it
// (monetary programmes, or the versioned output's totalPay).
const getCurrentPay = (bonusOverview: BonusTypes.BonusOverview) => {
  const { actualRewardOutput, bonusProgram } = bonusOverview;
  if ('totalPay' in actualRewardOutput) {
    return actualRewardOutput.totalPay;
  }
  return bonusProgram.rewardType === 'points'
    ? undefined
    : actualRewardOutput.totalReward;
};

const ChangeRow = ({
  label,
  before,
  after,
  testId,
}: {
  label: string;
  before: string;
  after: string;
  testId?: string;
}) => (
  <Row spaceBetween centerAlign style={{ gap: 16 }} data-testid={testId}>
    <Typography.Body type={'Body 13'} color={'var(--color-text-muted)'}>
      {label}
    </Typography.Body>
    <Row centerAlign style={{ gap: 6 }}>
      <Typography.Body type={'Body 13'} color={'var(--color-text-muted)'}>
        {`${before} →`}
      </Typography.Body>
      <Typography.Body type={'Body 14 Bold'}>{after}</Typography.Body>
    </Row>
  </Row>
);

/**
 * The confirm step for a recognition payment, opened from the mode header's
 * Add button (like ConfirmOverrideDialog for overrides): what will be paid to
 * whom, its effect on the driver's pay and the period's budget, and the note.
 */
const ConfirmAdditionalPaymentDialog = ({ close }: { close: () => void }) => {
  const { bonusOverview } = useContext(BonusOverviewContext);
  const { response, validAmount, note, label, isSaving, addPayment } =
    useContext(AdditionalPaymentsModeContext);
  const formatAmount = useFormatPaymentAmount();

  const onConfirmClicked = useCallback(async () => {
    await addPayment();
    // On success the mode has already closed; on failure the error shows in
    // the form.
    close();
  }, [addPayment, close]);

  if (!bonusOverview || !response || validAmount === undefined) {
    return null;
  }

  const driverName =
    bonusOverview.actualRewardOutput.driverName || bonusOverview.driver;
  const isDisqualified =
    bonusOverview.actualRewardOutput.eligibleForBonus === 'disqualified';
  const currentPay = getCurrentPay(bonusOverview);

  return (
    <div
      style={{ padding: '20px 24px', width: 360, boxSizing: 'border-box' }}
      data-testid={'confirm-additional-payment-dialog'}
    >
      <div style={{ marginBottom: 16 }}>
        <Typography.Header type={'H3'}>
          {`Add a ${formatAmount(validAmount)} additional payment`}
        </Typography.Header>
        <Typography.Body type={'Body 13'} color={'var(--color-text-muted)'}>
          {label ? `${label} · to ${driverName}` : `to ${driverName}`}
        </Typography.Body>
      </div>
      {(isDisqualified || currentPay !== undefined) && (
        <div
          style={{
            backgroundColor: 'var(--color-bg-tertiary)',
            borderRadius: 10,
            padding: 16,
          }}
        >
          {isDisqualified ? (
            <Typography.Body type={'Body 13'}>
              {`Adds ${formatAmount(0)} while ${driverName} is disqualified`}
            </Typography.Body>
          ) : (
            currentPay !== undefined && (
              <ChangeRow
                label={'Bonus pay'}
                before={formatAmount(currentPay)}
                after={formatAmount(currentPay + validAmount)}
                testId={'additional-payment-impact'}
              />
            )
          )}
        </div>
      )}
      {note.trim() !== '' && (
        <div
          style={{
            marginTop: 16,
            paddingLeft: 12,
            borderLeft: '2px solid var(--color-border-primary)',
          }}
        >
          <Typography.Body
            type={'Body 13'}
          >{`"${note.trim()}"`}</Typography.Body>
        </div>
      )}
      <Row style={{ justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
        <Button
          size={'Small'}
          type={'Secondary'}
          label={'Cancel'}
          onClick={close}
          isDisabled={isSaving}
        />
        <Button
          size={'Small'}
          type={'Primary'}
          label={'Confirm Payment'}
          onClick={onConfirmClicked}
          isLoading={isSaving}
          testId={'confirm-additional-payment'}
        />
      </Row>
    </div>
  );
};

export default ConfirmAdditionalPaymentDialog;
